import Phaser from "phaser";
import { playSound } from "../utils/sound-utils";

export interface CraftingButtonConfig {
  scene: Phaser.Scene;
  x: number;
  y: number;
  text: string;
  icon: string;
  iconFrame?: number | string;
  style?: "weapon" | "ammo" | "support";
  onClick: () => void;
}

// ── Layout constants for larger crafting buttons ───────────────────────────
const BTN_W = 100;
const BTN_H = 100;
const BTN_TOP = -40;         // card top relative to container centre
const BTN_RADIUS = 18;

// Dark outer ring (extends beyond fill — creates the elevated "thick border" look)
const OUTER_W = 5;
// Bright inner rim (stroked just inside the fill edge)
const INNER_W = 3;
const INNER_INSET = 2;

// Drop shadow offset
const SHADOW_DX = 3;
const SHADOW_DY = 8;

// Icon centered in upper portion of card
const ICON_SIZE = 105;
const ICON_Y = -30;

// Label near card bottom
const LABEL_Y = 30;
const LABEL_FONT_SIZE = "18px";

// Specular dot (small shine in upper-left of card)
const DOT_R = 5;
const DOT_INSET_Y = BTN_TOP + 10;

// Hit area covers icon overflow + padding
const HIT_TOP = -70;
const HIT_H = 160;
// ─────────────────────────────────────────────────────────────────────────────

export class CraftingButton extends Phaser.GameObjects.Container {
  private background: Phaser.GameObjects.Graphics;
  private border: Phaser.GameObjects.Graphics;
  private shine: Phaser.GameObjects.Graphics;
  private iconImage: Phaser.GameObjects.Image;
  private label: Phaser.GameObjects.Text;
  private config: CraftingButtonConfig;
  private isActive: boolean = false;
  private isPressed: boolean = false;

  constructor(config: CraftingButtonConfig) {
    super(config.scene, config.x, config.y);

    this.config = {
      style: "weapon",
      ...config,
    };

    this.background = new Phaser.GameObjects.Graphics(this.scene);
    this.border = new Phaser.GameObjects.Graphics(this.scene);
    this.shine = new Phaser.GameObjects.Graphics(this.scene);

    this.iconImage = new Phaser.GameObjects.Image(
      this.scene,
      0,
      ICON_Y,
      config.icon,
      config.iconFrame,
    );
    this.iconImage.setDisplaySize(ICON_SIZE, ICON_SIZE);
    this.iconImage.setOrigin(0.5);

    this.label = new Phaser.GameObjects.Text(
      this.scene,
      0,
      LABEL_Y,
      config.text,
      {
        fontSize: LABEL_FONT_SIZE,
        fontFamily: "Anton, sans-serif",
        color: "#ffffff",
        align: "center",
        stroke: "#000000",
        strokeThickness: 5,
        wordWrap: { width: BTN_W - 12 },
      },
    );
    this.label.setOrigin(0.5);

    const layers: Phaser.GameObjects.GameObject[] = [
      this.background,
      this.border,
      this.shine,
      this.iconImage,
      this.label,
    ];
    this.add(layers);

    this.updateAppearance();
    this.setupInteractions();

    config.scene.add.existing(this);
  }

  private getColors(): {
    outerBorder: number;
    fill: number;
    innerRim: number;
    labelColor: string;
  } {
    const { style } = this.config;
    const pressed = this.isPressed;
    const active = this.isActive;

    // Color scheme based on crafting category
    if (style === "ammo") {
      return {
        outerBorder: 0x1a0f00,
        fill:        pressed ? 0x663300 : 0x994c00,
        innerRim:    pressed ? 0xff9933 : 0xffb366,
        labelColor:  "#ffffff",
      };
    }
    if (style === "support") {
      return {
        outerBorder: 0x0f1a00,
        fill:        pressed ? 0x3d6600 : 0x5c9900,
        innerRim:    pressed ? 0x99ff00 : 0xccff33,
        labelColor:  "#ffffff",
      };
    }
    // weapon (default)
    if (active) {
      return {
        outerBorder: 0x030810,
        fill:        0x0f3a80,
        innerRim:    0x7fd8f0,
        labelColor:  "#7fd8f0",
      };
    }
    if (pressed) {
      return {
        outerBorder: 0x030508,
        fill:        0x0c1c38,
        innerRim:    0x3a6090,
        labelColor:  "#c8dff0",
      };
    }
    // Default weapon — vivid blue
    return {
      outerBorder: 0x060a12,
      fill:        0x1d55a8,
      innerRim:    0x5b9bd5,
      labelColor:  "#ffffff",
    };
  }

  private updateAppearance(): void {
    const c = this.getColors();

    this.background.clear();
    this.border.clear();
    this.shine.clear();

    // ① Drop shadow — offset rect behind the card
    this.background.fillStyle(0x000000, 0.55);
    this.background.fillRoundedRect(
      -BTN_W / 2 + SHADOW_DX,
      BTN_TOP + SHADOW_DY,
      BTN_W,
      BTN_H,
      BTN_RADIUS,
    );

    // ② Dark outer ring — slightly larger than the fill, creates the thick dark border
    this.background.fillStyle(c.outerBorder, 1);
    this.background.fillRoundedRect(
      -BTN_W / 2 - OUTER_W,
      BTN_TOP - OUTER_W,
      BTN_W + OUTER_W * 2,
      BTN_H + OUTER_W * 2,
      BTN_RADIUS + OUTER_W,
    );

    // ③ Main fill
    this.background.fillStyle(c.fill, 1);
    this.background.fillRoundedRect(-BTN_W / 2, BTN_TOP, BTN_W, BTN_H, BTN_RADIUS);

    // ④ Inner bright rim — inset stroke
    this.border.lineStyle(INNER_W, c.innerRim, 1);
    this.border.strokeRoundedRect(
      -BTN_W / 2 + INNER_INSET,
      BTN_TOP + INNER_INSET,
      BTN_W - INNER_INSET * 2,
      BTN_H - INNER_INSET * 2,
      BTN_RADIUS - 1,
    );

    // ⑤ Specular dot (shine)
    this.shine.fillStyle(c.innerRim, 0.4);
    this.shine.fillCircle(-BTN_W / 4, DOT_INSET_Y, DOT_R);

    // Update label color
    this.label.setColor(c.labelColor);
  }

  private setupInteractions(): void {
    this.setInteractive(
      new Phaser.Geom.Rectangle(
        -BTN_W / 2,
        HIT_TOP,
        BTN_W,
        HIT_H,
      ),
      Phaser.Geom.Rectangle.Contains,
    );

    this.on("pointerover", () => {
      this.setActive(true);
    });

    this.on("pointerout", () => {
      this.setActive(false);
      this.setPressed(false);
    });

    this.on("pointerdown", () => {
      this.setPressed(true);
    });

    this.on("pointerup", () => {
      this.setPressed(false);
      if (this.isActive) {
        playSound(this.scene, "menu-click-sound");
        this.config.onClick();
      }
    });
  }

  setActive(active: boolean): this {
    if (this.isActive !== active) {
      this.isActive = active;
      this.updateAppearance();
      this.scene.tweens.killTweensOf(this);
      if (active) {
        this.scene.tweens.add({
          targets: this,
          scaleX: 1.05,
          scaleY: 1.05,
          duration: 100,
          ease: "Quad.easeOut",
        });
      } else {
        this.scene.tweens.add({
          targets: this,
          scaleX: 1,
          scaleY: 1,
          duration: 100,
          ease: "Quad.easeOut",
        });
      }
    }
    return this;
  }

  setPressed(pressed: boolean): this {
    if (this.isPressed !== pressed) {
      this.isPressed = pressed;
      this.updateAppearance();
    }
    return this;
  }

  setLabel(text: string): void {
    this.label.setText(text);
  }

  setIcon(icon: string, frame?: number | string): void {
    this.iconImage.setTexture(icon, frame);
  }

  setStyle(style: "weapon" | "ammo" | "support"): void {
    this.config.style = style;
    this.updateAppearance();
  }

  setVisible(visible: boolean): this {
    super.setVisible(visible);
    return this;
  }

  destroy(): void {
    super.destroy();
  }
}
